import { useState, useMemo } from "react";
import { MapPin, ArrowUpRight } from "lucide-react";

const PROJECTS = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    title: "Vivienda Unifamiliar Moderna",
    category: "Obra Nueva",
    location: "Cunit, Tarragona",
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    title: "Rehabilitación Integral con Piscina",
    category: "Fachadas",
    location: "Calafell, Tarragona",
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1598635031829-4bfae29d33eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    title: "Residencial con Jardines",
    category: "Obra Nueva",
    location: "El Vendrell, Tarragona",
  },
  {
    id: 4,
    url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    title: "Baño de Lujo con Alicatado",
    category: "Interiores",
    location: "Sitges, Barcelona",
  },
  {
    id: 5,
    url: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    title: "Estructura y Albañilería",
    category: "Reformas Integrales",
    location: "Tarragona Centro",
  },
  {
    id: 6,
    url: "https://images.unsplash.com/photo-1621983209989-f3b263b93cf4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    title: "Fachada SATE Térmica",
    category: "Fachadas",
    location: "Cunit, Tarragona",
  },
];

const CATEGORIES = ["Todos", "Obra Nueva", "Reformas Integrales", "Fachadas", "Interiores"];

export default function Portfolio() {
  const [active, setActive] = useState("Todos");

  const items = useMemo(
    () => (active === "Todos" ? PROJECTS : PROJECTS.filter((p) => p.category === active)),
    [active]
  );

  return (
    <section id="proyectos" data-testid="portfolio-section" className="relative py-24 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F26522]/10 text-[#D95212] text-xs font-mono-accent uppercase tracking-[0.18em] mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F26522]" />
              Proyectos Recientes
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1528] tracking-tight leading-[1.05]">
              Obras finalizadas que{" "}
              <span className="text-[#F26522]">hablan por sí solas.</span>
            </h2>
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={
                  active === c
                    ? "px-4 py-2 rounded-full bg-[#0B1528] text-white text-sm font-semibold"
                    : "px-4 py-2 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 text-sm font-medium transition-colors"
                }
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p, i) => (
            <a
              key={p.id}
              data-testid={`portfolio-card-${p.id}`}
              href="#contacto"
              className={
                "group relative block rounded-2xl overflow-hidden lift " +
                (i % 5 === 0 ? "sm:col-span-2 lg:row-span-2" : "")
              }
            >
              <div className={i % 5 === 0 ? "aspect-[4/5]" : "aspect-[4/3]"}>
                <img
                  src={p.url}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528] via-[#0B1528]/40 to-transparent opacity-90" />

              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#F26522] text-white text-[11px] font-mono-accent uppercase tracking-[0.15em]">
                  {p.category}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 text-white">
                <div className="flex items-center gap-1.5 text-xs text-white/70 font-mono-accent mb-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {p.location}
                </div>
                <div className="flex items-end justify-between gap-3">
                  <h3 className="font-display text-lg sm:text-xl font-bold leading-tight">
                    {p.title}
                  </h3>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/20 group-hover:bg-[#F26522] group-hover:border-[#F26522] transition-colors shrink-0">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
